import { AlertTriangle, RefreshCw, Store, Wifi, WifiOff } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { api, formatApiError } from "@/lib/api";
import { ManagerDashboard } from "@/components/ManagerDashboard";
import { useAuth } from "@/components/AuthProvider";


const formatCurrency = (value) => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
}).format(value || 0);

const formatDateTime = (value) => value
  ? new Intl.DateTimeFormat("id-ID", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
  : "Belum tersedia";

export default function DashboardPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStoreId, setSelectedStoreId] = useState("all");

  const loadOverview = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await api.get("/dashboard/overview");
      setOverview(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const activeOverview = useMemo(() => {
    if (!overview || selectedStoreId === "all") {
      return overview;
    }
    const stores = overview.stores.filter((store) => store.store_id === selectedStoreId);
    const online = stores.filter((store) => store.connection_status === "online");
    return {
      ...overview,
      stores,
      total_sales: online.reduce((total, store) => total + store.total_sales, 0),
      transaction_count: online.reduce((total, store) => total + store.transaction_count, 0),
      online_store_count: online.length,
      offline_store_count: stores.length - online.length,
      expected_store_count: stores.length,
      is_complete: online.length === stores.length,
    };
  }, [overview, selectedStoreId]);

  if (user.role === "MANAGER") {
    return <ManagerDashboard />;
  }

  return (
    <main className="workspace" data-testid="dashboard-page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">RINGKASAN OPERASIONAL</p>
          <h1 data-testid="dashboard-page-title">Semua toko, satu pandangan</h1>
        </div>
        <button
          className="icon-text-button"
          data-testid="dashboard-refresh-button"
          disabled={isLoading}
          onClick={loadOverview}
          type="button"
        >
          <RefreshCw aria-hidden="true" className={isLoading ? "spin" : ""} size={17} />
          Perbarui
        </button>
      </section>

      {error && <p className="form-error" data-testid="dashboard-error" role="alert">{error}</p>}
      {isLoading && <p className="loading-note" data-testid="dashboard-loading">Membaca status toko…</p>}

      {activeOverview && (
        <>
          <section className="filter-band" data-testid="dashboard-filter-band">
            <label htmlFor="dashboard-store-filter">Tampilan toko</label>
            <select
              data-testid="dashboard-store-filter"
              id="dashboard-store-filter"
              onChange={(event) => setSelectedStoreId(event.target.value)}
              value={selectedStoreId}
            >
              <option value="all">Semua toko yang diizinkan</option>
              {overview.stores.map((store) => (
                <option key={store.store_id} value={store.store_id}>{store.name}</option>
              ))}
            </select>
          </section>

          {!activeOverview.is_complete && (
            <section className="incomplete-banner" data-testid="incomplete-data-alert" role="alert">
              <AlertTriangle aria-hidden="true" size={20} />
              <p>
                Total hanya mencakup {activeOverview.online_store_count} dari {activeOverview.expected_store_count}
                {" "}toko. Data toko offline belum termasuk.
              </p>
            </section>
          )}

          <section className="metric-grid" aria-label="Ringkasan angka toko">
            <article className="metric-card" data-testid="combined-sales-card">
              <span>Total penjualan</span>
              <strong data-testid="combined-sales-value">{formatCurrency(activeOverview.total_sales)}</strong>
              <small>Hanya toko yang online</small>
            </article>
            <article className="metric-card" data-testid="combined-transactions-card">
              <span>Transaksi</span>
              <strong data-testid="combined-transactions-value">
                {activeOverview.transaction_count.toLocaleString("id-ID")}
              </strong>
              <small>Transaksi terbaru hari ini</small>
            </article>
            <article className="metric-card" data-testid="store-availability-card">
              <span>Ketersediaan toko</span>
              <strong data-testid="store-availability-value">
                {activeOverview.online_store_count}/{activeOverview.expected_store_count}
              </strong>
              <small>{activeOverview.offline_store_count} toko offline</small>
            </article>
          </section>

          <section className="data-section" aria-labelledby="store-status-title">
            <div className="section-heading">
              <div>
                <p className="eyebrow">KONEKSI TOKO</p>
                <h2 id="store-status-title">Status toko saat ini</h2>
              </div>
              <span data-testid="dashboard-store-count">{activeOverview.stores.length} toko</span>
            </div>
            <div className="store-status-list" data-testid="dashboard-store-list">
              {activeOverview.stores.map((store) => {
                const online = store.connection_status === "online";
                const Icon = online ? Wifi : WifiOff;
                return (
                  <article className="store-status-row" data-testid={`dashboard-store-${store.store_id}`} key={store.store_id}>
                    <div className="store-name-block">
                      <span className={`connection-mark ${online ? "online" : "offline"}`}>
                        <Icon aria-hidden="true" size={16} />
                      </span>
                      <div>
                        <strong>{store.name}</strong>
                        <small>{store.code} · diperiksa {formatDateTime(store.last_checked_at)}</small>
                      </div>
                    </div>
                    <div className="store-numbers">
                      <span>{online ? formatCurrency(store.total_sales) : "Data belum tersedia"}</span>
                      <small>{online ? `${store.transaction_count} transaksi` : store.last_error}</small>
                    </div>
                  </article>
                );
              })}
              {!activeOverview.stores.length && (
                <p className="empty-state" data-testid="dashboard-empty-store-list">
                  <Store aria-hidden="true" size={18} /> Belum ada toko yang dapat diakses.
                </p>
              )}
            </div>
          </section>
        </>
      )}
    </main>
  );
}